/* ═══════════════════════════════════════════════════════════
   Käyttöliittymän kieli — YKSI PAIKKA (T439, päätös 105; pääChatin toimeksianto Tonin välittämänä 7.10.2026).

   `t(avain)` palauttaa tekstin nykyisellä kielellä. Avain on vakaa tunniste (`kirjautuminen.kirjaudu`), ei suomenkielinen
   lause, ja avainjoukko on TYYPPI (`KaannosAvain`): kirjoitusvirhe avaimessa on käännösvirhe, kuten moduulitunnuksissa.

   Kielen valinta, ensimmäinen joka on asetettu:
     1. käyttäjän oma kieli (`kayttaja.kieli`, palvelimella, valitaan Omista tiedoista)
     2. ennen kirjautumista tässä selaimessa valittu kieli (localStorage — vain kirjautumissivua varten)
     3. instanssin kieli (`brandi.kieli`)
     4. suomi
   Puuttuva käännös → suomi. Tyhjää ei koskaan näytetä, ja kehitystilassa puuttuva merkitään «⟦…⟧», jotta sen näkee.

   ⛔ Kieli on käyttöliittymän. Tallennettu aineisto ja tulosteet ovat kohteen dokumentaation kielellä (päätös 105, vaihe c),
   eikä tämä moduuli koske niihin.
   ═══════════════════════════════════════════════════════════ */

import { KAANNOKSET, type KaannosAvain } from "./kaannokset";
import { LT } from "./kaannokset_lt";
import { ET } from "./kaannokset_et";
import { KAYTTOLIITTYMAN_KIELET, type Kieli } from "./shared/types";

export type { KaannosAvain };
export { KAYTTOLIITTYMAN_KIELET };
export type { Kieli };

/** Kielen oma nimi omalla kielellään — valitsin näyttää «English», ei «englanti». */
export const KIELEN_NIMI: Record<Kieli, string> = { fi: "Suomi", en: "English", lt: "Lietuvių", et: "Eesti" };

/** Lukujen ja päivien lokaali kielen mukaan. */
export const LOKAALI: Record<Kieli, string> = { fi: "fi-FI", en: "en-GB", lt: "lt-LT", et: "et-EE" };

/* Liettua (Toni 11.10.2026 «liettua GO laajuuden mukaan»): kielen valittavuus on instanssin asetus (palvelimen KIELET,
   saadaan kirjautumisvastauksesta tai /api/brandista). Oletus fi, en. Kaikki käännökset ovat yhteisiä. */
let valittavat: Kieli[] = ["fi", "en"];
export function asetaValittavat(lista?: readonly string[] | null): void {
  const l = (lista ?? []).filter(onKieli);
  if (l.length) valittavat = l;
}
export const valittavatKielet = (): Kieli[] => valittavat;

const SELAIMEN_AVAIN = "sampo.kieli";
let nykyinen: Kieli = "fi";

export function onKieli(k: unknown): k is Kieli {
  return typeof k === "string" && (KAYTTOLIITTYMAN_KIELET as readonly string[]).includes(k);
}

/**
 * Aseta kieli: käyttäjän kieli voittaa, sitten selaimen kirjautumissivun valinta, sitten instanssin kieli.
 * Kirjautumissivulla tunnusta ei vielä tiedetä (Norjan erä 8.10.2026): siellä luetaan laitteen kieli (fi/en) ennen
 * instanssin kieltä, oletus suomi. Kirjautuneelle laitteen kieli EI vaikuta — kieli on työnjohdon asettama tai oma valinta,
 * eikä englanninkielinen puhelin saa vaihtaa suomalaisen työntekijän näkymää.
 */
export function asetaKieli(kayttajan?: string | null, instanssin?: string | null, kirjautumaton = false): Kieli {
  let selaimen: string | null = null;
  try { selaimen = localStorage.getItem(SELAIMEN_AVAIN); } catch { /* yksityinen ikkuna */ }
  let laitteen: string | null = null;
  if (kirjautumaton) {
    try { laitteen = (navigator.languages?.[0] ?? navigator.language ?? "").slice(0, 2).toLowerCase(); } catch { /* ei selainta */ }
  }
  const k = [kayttajan, selaimen, laitteen, instanssin].find((x): x is Kieli => onKieli(x) && valittavat.includes(x)) ?? "fi";
  nykyinen = k;
  try { document.documentElement.lang = k; } catch { /* testit ilman DOMia */ }
  return k;
}

/** Kirjautumissivun valinta: tallentuu tähän selaimeen, ei tunnukselle (tunnusta ei vielä tiedetä). */
export function asetaSelaimenKieli(k: Kieli): void {
  try { localStorage.setItem(SELAIMEN_AVAIN, k); } catch { /* ei talteen, toimii silti tämän sivun ajan */ }
  nykyinen = k;
  try { document.documentElement.lang = k; } catch { /* */ }
}

export const kieli = (): Kieli => nykyinen;
export const lokaali = (): string => LOKAALI[nykyinen];

const KEHITYS = typeof location !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(location.hostname);

/** Teksti nykyisellä kielellä. `{nimi}`-paikat täytetään muuttujista. */
/** Tekstin käännös kielellä, tai undefined. Liettua on omassa tiedostossaan (konekäännös, tarkistamatta kunnes merkitty). */
export function kaannos(avain: KaannosAvain, k: Kieli): string | undefined {
  if (k === "lt") return LT[avain] || undefined;
  if (k === "et") return ET[avain] || undefined;
  return (KAANNOKSET[avain] as Partial<Record<Kieli, string>>)[k] || undefined;
}

/** Teksti nykyisellä kielellä. Varakieli (Toni 11.10.2026): muille kuin suomelle englanti, sitten suomi. */
export function t(avain: KaannosAvain, muuttujat?: Record<string, string | number>): string {
  const rivi = KAANNOKSET[avain] as Partial<Record<Kieli, string>> & { fi: string };
  let teksti = kaannos(avain, nykyinen) ?? (nykyinen !== "fi" ? rivi.en || undefined : undefined);
  if (teksti === undefined || teksti === "") teksti = KEHITYS && nykyinen !== "fi" ? `⟦${rivi.fi}⟧` : rivi.fi;
  if (muuttujat) teksti = teksti.replace(/\{(\w+)\}/g, (_, n: string) => (n in muuttujat ? String(muuttujat[n]) : `{${n}}`));
  return teksti;
}

/** Esimerkkiteksti kenttään (Toni 11.10.2026 «esimerkkitekstin eteen esim., jotta tajuaa heti, että siihen kuuluu
 *  kirjoittaa jotain»): «esim. » / «e.g. » eteen, ellei teksti jo ala niin. Vain ESIMERKKIARVOILLE — ohje («Hae
 *  nimellä…») ei ole esimerkki eikä saa etuliitettä. Kävely mittaa: placeholder alkaa «esim.»/«e.g.» tai päättyy «…». */
export function esim(teksti: string): string {
  if (!teksti) return teksti;
  if (/^(esim\.|e\.g\.|nt\s)/i.test(teksti.trim())) return teksti;
  // Viro (Toni 18.10.2026 klo 13.00): «nt» (näiteks). Koskee vain viroa; muut kielet ennallaan.
  return (nykyinen === "en" ? "e.g. " : nykyinen === "et" ? "nt " : "esim. ") + teksti;
}

/** Kannasta tuleva järjestelmäteksti (moduulin ja oikeuden nimi ja kuvaus, 10.10.2026): suomenkielinen teksti on
 *  kannassa, ja muu kieli luetaan käännöstaulukosta avaimella `kanta.<taulu>.<tunnus>.<kenttä>`. Puuttuva avain tai
 *  suomi → kannan teksti sellaisenaan. Kanta ei muutu, ja uusi moduuli näkyy suomeksi kunnes avain lisätään. */
export function kantaTeksti(taulu: "moduuli" | "oikeus", tunnus: string, kentta: "nimi" | "kuvaus", fi: string): string {
  // 12.10.2026 (T530): myös suomi luetaan repon käännöksestä kun avain on — yksi totuus on kaannokset.ts (Toni 10.10.2026), ja
  // kannan kuvaukseen jäänyt sisäinen merkintä («(Toni 22.9.2026)») näkyi asiakkaan pääkäyttäjälle. Kanta on varakeino.
  const avain = `kanta.${taulu}.${tunnus}.${kentta}`;
  if (!(avain in KAANNOKSET)) return fi;
  return nykyinen === "fi" ? KAANNOKSET[avain as KaannosAvain].fi : t(avain as KaannosAvain);
}

/** Tuloste kohteen dokumentaation kielellä (päätös 105 vaihe c), ei käyttöliittymän. Kohteella ei vielä ole
 *  dokumentaatiokieltä, joten tuloste on suomeksi riippumatta katsojan kielestä — avain silti taulukossa, jotta
 *  dokumentaatiokieli on yhden parametrin muutos. */
export function tDok(avain: KaannosAvain, muuttujat?: Record<string, string | number>): string {
  let s: string = KAANNOKSET[avain].fi;
  if (muuttujat) for (const [k, v] of Object.entries(muuttujat)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/**
 * Palvelimen virhe omalla kielellä: koneluettava `virhe`-koodi on käännöksen avain (`virhe.<koodi>`), ja palvelimen
 * suomenkielinen `viesti` on varalla. Ei toista viestijoukkoa palvelimelle (suunnitelma 7.10. §2).
 */
export function virheTeksti(koodi: string | null | undefined, viesti: string): string {
  if (nykyinen === "fi" || !koodi) return viesti;
  const avain = `virhe.${koodi}` as KaannosAvain;
  return avain in KAANNOKSET ? t(avain) : viesti;
}
